/**
 * Antwortformen der API, so wie die Oberflaeche sie braucht.
 *
 * Bewusst nur das, was hier gelesen wird. Die verbindliche Wahrheit steht in
 * @bqs/contracts und in den Fachdiensten der API.
 */

export type Role = 'account_user' | 'account_admin' | 'sales' | 'operator_admin';

export interface SessionUser {
  readonly userId: string;
  readonly email: string;
  readonly displayName: string;
  readonly role: Role;
  readonly accountId: string | null;
  readonly accountName: string | null;
  readonly mustChangePassword: boolean;
}

export interface LoginResponse {
  readonly accessToken: string;
  readonly expiresIn: number;
  readonly user: SessionUser;
}

export interface ProductOption {
  readonly productId: string;
  readonly code: string;
  readonly label: string;
  readonly typeLabel: string;
  /** Schluessel der Schnittdarstellung, aus den Stammdaten. */
  readonly imageRef: string | null;
  readonly standardCode: string | null;
  readonly standardLabel: string | null;
  readonly articleCount: number;
}

export interface MaterialOption {
  readonly code: string;
  readonly label: string;
  readonly articleCount: number;
}

export interface VariantMeasure {
  readonly key: string;
  readonly label: string;
  readonly value: string;
  readonly unit: string | null;
}

export interface CatalogVariantRow {
  readonly variantId: string;
  readonly sizeLabel: string;
  readonly pressureLabel: string | null;
  readonly measures: readonly VariantMeasure[];
  readonly sourceText: string;
  readonly article: { readonly articleId: string; readonly articleNumber: string } | null;
}

export interface CatalogTable {
  readonly productId: string;
  readonly productLabel: string;
  readonly imageRef: string | null;
  readonly standardLabel: string | null;
  readonly materialCode: string;
  readonly materialLabel: string;
  readonly rows: readonly CatalogVariantRow[];
}

export interface SelectedPosition {
  readonly articleId: string;
  readonly articleNumber: string;
  readonly quantity: string;
}

/**
 * G-ST-P/OE, ADR-0022: eine konfigurierte Dichtung im Warenkorb -- EINE
 * zusammengehoerige Zeile, obwohl sie sich serverseitig in zwei Belegpositionen
 * (Dichtlippe, Stuetzring) aufteilt. Wird im Warenkorb nur als Ganzes bearbeitet
 * oder entfernt, nie ihre beiden Preisbestandteile einzeln.
 *
 * `localId` ersetzt `articleId` -- es gibt keinen Katalogartikel. Die Anzeige
 * (Bezeichnung, Profil, Werkstoff) wird aus den gespeicherten Feldern und der
 * Profilkonfiguration abgeleitet, nicht separat vorgehalten.
 */
export interface OeSealCartLine {
  readonly localId: string;
  readonly targetOuterDiameterMm: string;
  readonly targetInnerDiameterMm: string;
  readonly quantity: string;
  readonly profileArticleCode: string;
  readonly ringMaterial: 'STEEL_1_4301' | 'STEEL_1_4571';
}

export type CartLine = SelectedPosition | OeSealCartLine;

export function isOeSealCartLine(line: CartLine): line is OeSealCartLine {
  return !('articleId' in line);
}

/** Eindeutiger Schluessel einer Warenkorbzeile, unabhaengig von ihrer Art. */
export function cartLineKey(line: CartLine): string {
  return isOeSealCartLine(line) ? line.localId : line.articleId;
}

/** Wandelt eine Warenkorbzeile in die Positionsform der API (Preisvorschau und
 *  Angebot verwenden dieselbe Form, siehe `quotePositionSchema`). */
export function toApiPosition(line: CartLine): Record<string, unknown> {
  if (isOeSealCartLine(line)) {
    return {
      kind: 'oe_seal' as const,
      targetOuterDiameterMm: line.targetOuterDiameterMm,
      targetInnerDiameterMm: line.targetInnerDiameterMm,
      quantity: line.quantity,
      profileArticleCode: line.profileArticleCode,
      ringMaterial: line.ringMaterial,
    };
  }
  return {
    kind: 'catalog' as const,
    articleNumber: line.articleNumber,
    quantity: line.quantity,
  };
}

export interface RejectedSelection {
  readonly variantId: string;
  readonly reason: string;
  readonly detail: string;
}

export interface CatalogSelectionResult {
  readonly positions: readonly SelectedPosition[];
  readonly rejected: readonly RejectedSelection[];
}

export interface ArticleSearchHit {
  readonly articleId: string;
  readonly articleNumber: string;
  readonly description: string;
  readonly productTypeCode: string;
  readonly productLabel: string;
  readonly standardLabel: string | null;
  readonly materialCode: string;
  readonly materialLabel: string;
  readonly sizeLabel: string | null;
  readonly pressureLabel: string | null;
  readonly imageRef: string | null;
}

export interface Paginated<T> {
  readonly items: readonly T[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
}

/** Ein Schnellfilter-Hashtag der Produktsuche. */
export interface QuickFilterTag {
  readonly key: string;
  readonly displayName: string;
  readonly description: string | null;
}

/** G-ST-P/OE: der tatsaechlich berechnete Wert und die verletzte Grenze einer
 *  abgelehnten Geometriepruefung -- siehe `OeGeometryFailureDetail` in
 *  `@bqs/pricing`. Nur gesetzt, wenn `status` eine der sieben
 *  Geometriegrenzverletzungen ist. */
export interface PricingPreviewItemDetail {
  readonly actual: string;
  readonly limit: string;
  readonly unit: string;
}

export interface PricingPreviewItem {
  readonly status: string;
  readonly articleNumber: string;
  readonly description?: string;
  readonly detail?: PricingPreviewItemDetail;
  readonly quantity?: string;
  readonly listPrice?: string;
  readonly discountPercent?: number;
  readonly discountSource?: string;
  readonly netUnitPrice?: string;
  readonly lineNet?: string;
  /** G-ST-P/OE, ADR-0022: nur gesetzt, wenn die Zeile aus einer OE-Position
   *  stammt und erfolgreich bepreist wurde. */
  readonly oeRole?: 'PROFILE' | 'RING';
  readonly geometryClass?: string;
  readonly ringInnerDiameterMm?: string;
  readonly ringOuterDiameterMm?: string;
  readonly ringWidthMm?: string;
  readonly totalProfileLengthM?: string;
}

export interface PricingPreview {
  readonly status: string;
  /** `null` bei einer Schnellangebot-Vorschau -- sie hat kein Konto. */
  readonly accountId: string | null;
  readonly effectiveDate: string;
  readonly goodsValue: string;
  readonly appliedMinOrderThreshold: string;
  readonly minOrderSurchargeApplies: boolean;
  readonly minOrderSurcharge: string;
  readonly packagingFreightAmount: string;
  /** false heisst: Es ist kein Betrag hinterlegt -- nicht, dass er null waere. */
  readonly packagingFreightKnown: boolean;
  readonly documentTotalNet: string;
  readonly items: readonly PricingPreviewItem[];
}

// --- Angebote, M6 ---------------------------------------------------------

export interface QuoteItem {
  readonly position: number;
  readonly articleNumber: string;
  readonly description: string;
  readonly quantity: string;
  readonly unitOfMeasure: string;
  readonly listPrice: string;
  readonly discountPercent: number;
  readonly discountSource: string;
  readonly netUnitPrice: string;
  readonly lineNet: string;
  /** G-ST-P/OE, ADR-0022: gesetzt, wenn die Zeile zu einer konfigurierten
   *  Dichtung gehoert. */
  readonly oeConfigurationId: string | null;
  readonly oeRole: 'PROFILE' | 'RING' | null;
}

export interface QuoteOeConfiguration {
  readonly id: string;
  readonly position: number;
  readonly targetOuterDiameterMm: string;
  readonly targetInnerDiameterMm: string;
  readonly quantity: string;
  readonly ringMaterial: 'STEEL_1_4301' | 'STEEL_1_4571';
  readonly ringInnerDiameterMm: string;
  readonly ringOuterDiameterMm: string;
  readonly ringWidthMm: string;
  readonly sheetThicknessMm: string;
  readonly geometryClass: string;
  readonly profileArticleCode: string;
  readonly totalProfileLengthM: string;
}

export interface Quote {
  readonly quoteId: string;
  readonly quoteNumber: string | null;
  readonly status: 'draft' | 'issued' | 'expired' | 'withdrawn';
  /** `null` beim Schnellangebot -- es hat kein Konto. */
  readonly accountId: string | null;
  /**
   * Schnellangebot: gesetzt genau dann, wenn `accountId` `null` ist -- der
   * Empfaenger ist dann reiner Belegtext. `null` heisst gewoehnliches Angebot.
   */
  readonly recipientName: string | null;
  readonly recipientNumber: string | null;
  readonly quoteDate: string | null;
  readonly effectiveDate: string;
  readonly validUntil: string | null;
  readonly issuedAt: string | null;
  readonly currency: string;
  readonly supersedesQuote: {
    readonly id: string;
    readonly quoteNumber: string | null;
  } | null;
  readonly goodsValue: string | null;
  readonly appliedMinOrderThreshold: string | null;
  readonly minOrderSurcharge: string | null;
  /** `null` heisst: kein Betrag hinterlegt. Nicht dasselbe wie `0,00`. */
  readonly packagingFreightAmount: string | null;
  readonly documentTotalNet: string | null;
  readonly pdfAvailable: boolean;
  readonly items: readonly QuoteItem[];
  readonly oeConfigurations: readonly QuoteOeConfiguration[];
}

export interface QuoteListEntry {
  readonly quoteId: string;
  readonly quoteNumber: string | null;
  readonly status: 'draft' | 'issued' | 'expired' | 'withdrawn';
  readonly quoteDate: string | null;
  readonly issuedAt: string | null;
  readonly documentTotalNet: string | null;
  readonly currency: string;
  readonly itemCount: number;
  readonly pdfAvailable: boolean;
}

/** Eintrag der zentralen Angebotsuebersicht, `GET /admin/quotes` (interne Rollen). */
export interface AdminQuoteListEntry extends QuoteListEntry {
  /** `null` beim Schnellangebot -- es hat kein Konto. */
  readonly accountId: string | null;
  /**
   * Ab Ausstellung die Kopfkopie vom Beleg, davor der aktuelle Kundenstamm oder --
   * beim Schnellangebot -- der frei genannte Empfaenger.
   */
  readonly accountName: string;
  readonly accountNumber: string;
  readonly createdAt: string;
  readonly createdByName: string;
}
